import React, { type FC, use, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Label, ListBox, Popover, Select, TextArea, TextField } from "@heroui/react";
import { Comment, CommentDot } from "@gravity-ui/icons";
import { type GQL } from "@binale-tech/shared";

import { CalendarItemStateControlContext } from "@domain/context/CalendarItemStateContext";
import { useColorItems } from "@ui-components/hooks/useColorItems";
import { type CalendarEvent } from "@accounting/common/calendar/types";

// SP3 of the calendar feature (FIBU-22): note + color popover, in-place (local
// `useState` draft, no ReactNode-in-state - the modal-pattern rule). Only
// touches `note`/`color` in the `calendarItemStateSet` call - never `done`,
// so a save here can never clobber a done state set by the checkbox
// affordance (design spec §III.6/§III.7). `note` is a working annotation,
// deliberately distinct from `calendarEvents.notes` (the event's own
// description) - kept under a different UI label ("Notiz", not "Notizen"),
// design spec §III.9.
//
// Color reuses the same DocumentColors palette + Select pattern as SP1's
// CalendarEventFormModal (useColorItems) rather than
// FormRecordNotesColorSelect's 3 fixed semantic colors (done/inProgress/task)
// - those carry unrelated meaning and don't cover the 5-value palette
// calendarItemState.color shares with calendarEvents.color (§III.5).

type Props = {
    event: CalendarEvent;
};

export const CalendarItemStatePopover: FC<Props> = ({ event }) => {
    const intl = useIntl();
    const { setItemState } = use(CalendarItemStateControlContext);
    const colorItems = useColorItems();

    const [note, setNote] = useState(event.note ?? "");
    const [color, setColor] = useState(event.color ?? "");

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                setNote(event.note ?? "");
                setColor(event.color ?? "");
            }
        },
        [event.note, event.color],
    );

    const handleColorChange = useCallback((v: string | number | null) => setColor(String(v ?? "")), []);

    const handleSave = useCallback(() => {
        setItemState({
            // `event.source` is SP1's plain CalendarEventSource union
            // ("user"/"derived"); `refType` on the wire is the generated
            // string enum with the same runtime values - see the cast note
            // in calendarItemState.ts.
            refType: event.source as GQL.ICalendarItemStateRefType,
            refId: event.stateKey,
            note: note.trim() || null,
            // `CalendarItemState.color` is a plain SDL `String` (not the
            // DocumentColors enum, see calendar.graphql), so no cast is needed
            // here - unlike `event.color` on the merged UI shape.
            color: color || null,
        });
    }, [setItemState, event.source, event.stateKey, note, color]);

    const triggerLabel = intl.formatMessage({ id: "app.home.calendar.state.noteTrigger" });

    return (
        <Popover onOpenChange={handleOpenChange}>
            <Button
                size="sm"
                variant="ghost"
                isIconOnly
                aria-label={triggerLabel}
                className="CalendarItemStatePopover__trigger shrink-0"
            >
                {event.note ? <CommentDot className="size-4" /> : <Comment className="size-4" />}
            </Button>
            <Popover.Content className="w-72">
                <Popover.Dialog className="flex flex-col gap-3">
                    <TextField fullWidth value={note} onChange={setNote}>
                        <Label>
                            <FormattedMessage id="app.home.calendar.state.noteLabel" />
                        </Label>
                        <TextArea rows={3} />
                    </TextField>
                    <Select fullWidth value={color} onChange={handleColorChange}>
                        <Label>
                            <FormattedMessage id="app.home.calendar.modal.fields.color" />
                        </Label>
                        <Select.Trigger>
                            <Select.Value />
                            <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox>
                                <ListBox.Item
                                    id=""
                                    textValue={intl.formatMessage({ id: "app.button.color.remove_color" })}
                                >
                                    <FormattedMessage id="app.button.color.remove_color" />
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                                {colorItems.map((item) => (
                                    <ListBox.Item key={item.key} id={item.key} textValue={item.key}>
                                        <span className="flex items-center gap-2">
                                            {item.icon}
                                            {item.label}
                                        </span>
                                        <ListBox.ItemIndicator />
                                    </ListBox.Item>
                                ))}
                            </ListBox>
                        </Select.Popover>
                    </Select>
                    <Button size="sm" onPress={handleSave} className="self-end">
                        <FormattedMessage id="app.button.save" />
                    </Button>
                </Popover.Dialog>
            </Popover.Content>
        </Popover>
    );
};
